import { useCallback, useEffect, useRef, useState } from "react";
import "./MediaLibraryPanel.css";
import {
  listFilesForPicker,
  uploadFile,
  updateFileMetadata,
  trashFile,
  getLibraries,
  createLibrary,
  mfmThumbUrl,
} from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

const TYPE_TABS = [
  { key: "IMAGE", label: "Imágenes", icon: "🖼", accept: "image/*" },
  { key: "VIDEO", label: "Vídeo",    icon: "🎬", accept: "video/*" },
  { key: "AUDIO", label: "Audio",    icon: "🎵", accept: "audio/*" },
];

const THUMB_ICON = { IMAGE: "🖼", VIDEO: "🎬", AUDIO: "🎵" };

// ── Library helpers ──────────────────────────────────────────────────────────

function libraryDisplayName(exhibicion) {
  return exhibicion?.titulo ? `[EXHI-${exhibicion.idExhibicion}] ${exhibicion.titulo}` : null;
}

async function findOrCreateLibrary(appCode, exhibicion) {
  if (!appCode || !exhibicion) return null;
  const name = libraryDisplayName(exhibicion);
  if (!name) return null;
  console.log(`[EXHI:MediaPanel] findOrCreateLibrary — appCode=${appCode} name="${name}"`);
  try {
    const libs = await getLibraries(appCode);
    const existing = libs.find((l) => l.name === name);
    if (existing) {
      console.log(`[EXHI:MediaPanel] findOrCreateLibrary — biblioteca existente id=${existing.idLibrary}`);
      return { id: existing.idLibrary, slug: existing.slug };
    }
    console.log(`[EXHI:MediaPanel] findOrCreateLibrary — creando nueva biblioteca...`);
    const created = await createLibrary(appCode, name, `Biblioteca de la exhibición "${exhibicion.titulo}"`);
    console.log(`[EXHI:MediaPanel] findOrCreateLibrary — creada id=${created?.idLibrary}`);
    return created ? { id: created.idLibrary, slug: created.slug } : null;
  } catch (err) {
    console.error("[EXHI:MediaPanel] findOrCreateLibrary — ERROR:", err);
    return null;
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export default function MediaLibraryPanel({ appCode, exhibicion }) {
  const [activeType,        setActiveType]        = useState("IMAGE");
  const [search,            setSearch]            = useState("");
  const [files,             setFiles]             = useState([]);
  const [loading,           setLoading]           = useState(false);
  const [uploading,         setUploading]         = useState(false);
  const [uploadProgress,    setUploadProgress]    = useState(0);
  const [editingUuid,       setEditingUuid]       = useState(null);
  const [editingTitle,      setEditingTitle]      = useState("");
  const [confirmDeleteUuid, setConfirmDeleteUuid] = useState(null);
  const [library,           setLibrary]           = useState(null); // { id, slug }

  const fileInputRef = useRef(null);

  // ── Find/create library per exhibition ──────────────────────────────────────
  useEffect(() => {
    if (!exhibicion || !appCode) return;
    findOrCreateLibrary(appCode, exhibicion).then(setLibrary);
  }, [exhibicion?.idExhibicion, appCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load files ──────────────────────────────────────────────────────────────
  const reloadFiles = useCallback(async (type, q, app, lib) => {
    setLoading(true);
    console.log(`[EXHI:MediaPanel] reloadFiles — tipo=${type} search="${q}" app=${app} libraryId=${lib?.id}`);
    try {
      const data = await listFilesForPicker({ app, library: lib?.id, mediaType: type, search: q, size: 50 });
      console.log(`[EXHI:MediaPanel] reloadFiles — ${data?.length ?? 0} ficheros`);
      setFiles(data ?? []);
    } catch (err) {
      console.error("[EXHI:MediaPanel] reloadFiles — ERROR:", err);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await listFilesForPicker({
          app: appCode,
          library: library?.id,
          mediaType: activeType,
          search,
          size: 50,
        });
        if (!cancelled) {
          console.log(`[EXHI:MediaPanel] autoReload — tipo=${activeType} search="${search}" → ${data?.length ?? 0} ficheros`);
          setFiles(data ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[EXHI:MediaPanel] autoReload — ERROR:", err);
          setFiles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeType, search, appCode, library]);

  // ── Upload ───────────────────────────────────────────────────────────────────
  const handleFileInputChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    // Derive title from filename (strip extension)
    const rawName = file.name || "";
    const title = rawName.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").trim();

    console.log(`[EXHI:MediaPanel] upload — fichero="${file.name}" tamaño=${file.size} tipo=${file.type} library=${library?.slug ?? "sin-biblioteca"}`);
    setUploading(true);
    setUploadProgress(0);
    try {
      await uploadFile(file, {
        appCode,
        library: library?.slug ?? undefined,  // backend expects library_slug
        title: title || undefined,
        onProgress: (pct) => setUploadProgress(pct),
      });
      console.log(`[EXHI:MediaPanel] upload — completado "${file.name}"`);
      toast.show({ message: "Fichero subido correctamente", type: "success" });
      await reloadFiles(activeType, search, appCode, library);
    } catch (err) {
      console.error("[EXHI:MediaPanel] upload — ERROR:", err);
      toast.show({ message: err?.message || "Error al subir fichero", type: "danger" });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [appCode, library, activeType, search, reloadFiles]);

  // ── Edit (rename) ────────────────────────────────────────────────────────────
  const startEdit = useCallback((uuid, title) => {
    setConfirmDeleteUuid(null);
    setEditingUuid(uuid);
    setEditingTitle(title || "");
  }, []);

  const commitEdit = useCallback(async (uuid) => {
    const trimmed = editingTitle.trim();
    setEditingUuid(null);
    if (!trimmed) return;
    console.log(`[EXHI:MediaPanel] commitEdit — uuid=${uuid} nuevoTitulo="${trimmed}"`);
    try {
      await updateFileMetadata(uuid, { title: trimmed });
      setFiles((prev) => prev.map((f) => f.uuid === uuid ? { ...f, title: trimmed } : f));
    } catch (err) {
      console.error("[EXHI:MediaPanel] commitEdit — ERROR:", err);
      toast.show({ message: "Error al actualizar el nombre", type: "danger" });
    }
  }, [editingTitle]);

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (uuid) => {
    setConfirmDeleteUuid(null);
    console.log(`[EXHI:MediaPanel] delete — uuid=${uuid}`);
    // Remove optimistically first — prevents thumbnail 409 and immediate reappearance
    setFiles((prev) => prev.filter((f) => f.uuid !== uuid));
    try {
      await trashFile(uuid);
      console.log(`[EXHI:MediaPanel] delete — enviado a papelera uuid=${uuid}`);
      toast.show({ message: "Fichero enviado a la papelera", type: "success" });
    } catch (err) {
      console.error("[EXHI:MediaPanel] delete — ERROR:", err);
      toast.show({ message: "Error al eliminar fichero", type: "danger" });
      // Restore list on failure
      await reloadFiles(activeType, search, appCode, library);
    }
  }, [activeType, search, appCode, library, reloadFiles]);

  // ── Drag start ───────────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, f) => {
    const payload = {
      uuid: f.uuid,
      titulo: f.title || f.originalName || "",
      mediaType: activeType,
      duracion: f.duracionOriginal ?? null,
      thumbUrl: f.thumbUrl ?? null,
    };
    console.log(`[EXHI:MediaPanel] dragStart — uuid=${f.uuid} tipo=${activeType} titulo="${payload.titulo}" duracion=${payload.duracion}`);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("application/exhi-media", JSON.stringify(payload));
  }, [activeType]);

  const activeTabMeta = TYPE_TABS.find((t) => t.key === activeType);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="exhi-ml">

      {/* Tab bar + Upload button */}
      <div className="exhi-ml-tabs">
        {TYPE_TABS.map((t) => (
          <button
            key={t.key}
            className={`exhi-ml-tab${activeType === t.key ? " active" : ""}`}
            onClick={() => { setActiveType(t.key); setSearch(""); }}
            title={t.label}
          >
            {t.icon}
          </button>
        ))}

        <button
          className="exhi-ml-upload-btn"
          title={`Subir ${activeTabMeta?.label}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? `${uploadProgress}%` : "＋"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={activeTabMeta?.accept}
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
      </div>

      {/* Upload progress bar */}
      {uploading && (
        <div className="exhi-ml-progress-track">
          <div className="exhi-ml-progress-bar" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {/* Search */}
      <div className="exhi-ml-search">
        <input
          className="form-control form-control-sm"
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* File list */}
      <div className="exhi-ml-content">
        {loading ? (
          <div className="exhi-ml-status">Cargando...</div>
        ) : files.length === 0 ? (
          <div className="exhi-ml-status text-muted">Sin resultados</div>
        ) : (
          files.map((f) => {
            const isEditing       = editingUuid       === f.uuid;
            const isConfirmDelete = confirmDeleteUuid === f.uuid;
            const displayName     = f.title || f.originalName || "—";
            const thumbSrc        = activeType !== "AUDIO" ? (f.thumbUrl || mfmThumbUrl(f.uuid)) : null;

            return (
              <div
                key={f.uuid}
                className="exhi-ml-item"
                title={displayName}
                draggable
                onDragStart={(e) => handleDragStart(e, f)}
              >
                {/* Thumbnail */}
                <div className="exhi-ml-item-thumb">
                  {thumbSrc ? (
                    <img
                      src={thumbSrc}
                      alt=""
                      className="exhi-ml-thumb-img"
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                    />
                  ) : null}
                  <span
                    className="exhi-ml-thumb-icon"
                    style={{ display: thumbSrc ? "none" : "flex" }}
                  >
                    {THUMB_ICON[activeType] ?? "🖼"}
                  </span>
                </div>

                {/* Name / edit input / confirm delete */}
                <div className="exhi-ml-item-main">
                  {isEditing ? (
                    <input
                      className="exhi-ml-edit-input"
                      value={editingTitle}
                      autoFocus
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => commitEdit(f.uuid)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")  commitEdit(f.uuid);
                        if (e.key === "Escape") setEditingUuid(null);
                      }}
                    />
                  ) : isConfirmDelete ? (
                    <div className="exhi-ml-confirm">
                      <span>¿Eliminar?</span>
                      <button className="exhi-ml-confirm-yes" onClick={() => handleDelete(f.uuid)}>Sí</button>
                      <button className="exhi-ml-confirm-no"  onClick={() => setConfirmDeleteUuid(null)}>No</button>
                    </div>
                  ) : (
                    <div className="exhi-ml-item-name">{displayName}</div>
                  )}
                </div>

                {/* Action buttons */}
                {!isEditing && !isConfirmDelete && (
                  <div className="exhi-ml-actions">
                    <button
                      className="exhi-ml-action-btn"
                      title="Renombrar"
                      onClick={() => startEdit(f.uuid, displayName)}
                    >✏</button>
                    <button
                      className="exhi-ml-action-btn exhi-ml-action-del"
                      title="Eliminar"
                      onClick={() => setConfirmDeleteUuid(f.uuid)}
                    >🗑</button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

